import { useAdminText } from './shared/useAdminText';
import { AICostPeriodFilter } from './shared/AICostPeriodFilter';
import { readCostPeriod, costDateRange, costPeriodParams, isValidCostPeriod } from '../utils/aiCostPeriod';
import { AdminTable } from './shared/AdminDataGrid';
import { useParams, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { TopToolbar, Title } from 'react-admin';
import { Alert, Typography, Button, Box, Chip, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, LinearProgress, Breadcrumbs, Link } from '@mui/material';
import Grid from '@mui/material/Grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { semantic } from '@rekindle/shared/theme';
import { adminApi } from '../providers/dataProvider';
import { formatCost } from '../utils/formatters';
import { StatCard } from './shared';
import { presentRecordedCost } from '../utils/aiCostPresentation';
import { AICostCalls } from './AICostCalls';
import { useAICostQuery } from '../hooks/useAICostQuery';

export function AICostUserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const [params] = useSearchParams();
  const period = readCostPeriod(params);
  const t = useAdminText();
  const query = useAICostQuery(
    ['ai-costs', 'user', userId, period], () => adminApi.getAICostUserDetail(userId!, costDateRange(period)), Boolean(userId) && isValidCostPeriod(period),
  );
  const data = query.data;
  return (
    <Box>
      <Title title={data ? `AI Costs - ${data.email}` : 'AI Costs'} />
      <TopToolbar>
        <Button component={RouterLink} to={`/ai-costs?${costPeriodParams(params)}`} startIcon={<ArrowBackIcon />} size="small">Back to AI Costs</Button>
        <Button onClick={() => void query.refetch()} disabled={query.isFetching}>Refresh summary</Button>
      </TopToolbar>
      <Box>
        <AICostPeriodFilter key={`period:${params}`} />
        {query.isFetching && <LinearProgress aria-label="Loading user cost summary" sx={{ mb: 2 }} />}
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{query.accessDenied ? 'Access to AI costs was denied. Reauthenticate before retrying.' : data ? 'Could not refresh the summary. Showing the last successful result.' : 'Could not load the user cost summary.'}</Alert>}
        {data && <>
          <Breadcrumbs sx={{ mb: 2 }}>
            <Link component={RouterLink} to={`/ai-costs?${costPeriodParams(params)}`} underline="hover" color="inherit">AI Costs</Link>
            <Typography color="text.primary">{data.email}</Typography>
          </Breadcrumbs>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12 }}>
              <StatCard title={t('selectedPeriodCost', 'Recorded cost for selected period')} value={presentRecordedCost(data.totalCost, data.costCoverage?.allTime, t).value}
                subtitle={presentRecordedCost(data.totalCost, data.costCoverage?.allTime, t).detail}
                icon={<AttachMoneyIcon sx={{ color: 'white' }} />} color={semantic.warning} />
            </Grid>
          </Grid>
          {data.costByModel.length > 0 && <>
            <Typography variant="h6" gutterBottom>{t('selectedPeriodModels', 'Recorded cost by model — selected period')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Model totals may include estimates and unclassified legacy amounts, and omit unknown costs.</Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <AdminTable size="small" aria-label="User cost by model">
                <TableHead><TableRow><TableCell>Model</TableCell><TableCell align="right">Recorded amount</TableCell><TableCell align="right">Calls</TableCell></TableRow></TableHead>
                <TableBody>{data.costByModel.map(model => <TableRow key={model.model} hover>
                  <TableCell><Chip label={model.model} size="small" variant="outlined" /></TableCell>
                  <TableCell align="right">{formatCost(model.totalCost)}</TableCell><TableCell align="right">{model.queryCount}</TableCell>
                </TableRow>)}</TableBody>
              </AdminTable>
            </TableContainer>
          </>}
        </>}
        {userId && <AICostCalls key={`${userId}:${params.toString()}`} userId={userId} />}
      </Box>
    </Box>
  );
}
